import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router";
import {
  loadUserDataIfMissing,
  userOnboardingStatus,
  safeChangeCompany,
  updateLoggedCompany,
  updateOwnerLoggedCompany,
} from "../../reducers/userReducer";
import Sidebar from "../partials/sidebar/sidebar";
import {DASHBOARD, LOGIN, ONBOARDING} from "../../routes/endpoints";
import Header from "../partials/header/header";
import { store } from "../../reducers/rootReducer";
import { toSubscribeOnLoad } from "../../reducers/subscribers";
import Notification from "../partials/Notification/notification";
import {
  setFakePermission,
  initialPermissionsLoad,
  isUserCompanyProcessing,
  processPermissionList,
  refreshPermissions,
} from "../../reducers/userCompanyReducer";
import { useTranslation } from "react-i18next";
import Loading from "./loading";
import {
  USER_AGENT,
  USER_EMPLOYEE,
  COMPANY_EXPORTER,
  COMPANY_IMPORTER,
} from "../../constants/config";
import moment from "moment";
import italianLocale from "moment/locale/it";
import {subscriptionUsageLimits} from "../../reducers/subscriptionReducer";
import {getWebPathParam} from "../../utils/converter";
import {useNavigate} from "react-router-dom";
import NotificationProgress from "../partials/Notification/notification-progress";
import { PanelLeft } from "lucide-react";
import { getCookie, setCookie } from "../../constants/cookie";
import { customersList } from "../../reducers/connectionReducer";

const SIDEBAR_STORAGE_KEY = "sidebarHidden";

const RequireAuth = () => {
  const { isAuthenticated, onboardingStatus, loggedCompany, detail, isSwitched } =
    useSelector((state) => state.user);

  const { status } =
      useSelector((state) => state.userCompany);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(() => {
    if (
      typeof window === "undefined" ||
      typeof window.localStorage === "undefined"
    ) {
      return false;
    }

    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
  });
  const location = useLocation();
  const dispatch = useDispatch();
  let navigate = useNavigate();
  const searchParams =
    typeof location?.search === "string"
      ? new URLSearchParams(location.search)
      : null;
  const iFrameViewQueryParam = searchParams?.get("iFrameView");
  const companySwitchQueryParam = searchParams?.get("companySwitch");
  const switchCompanyQueryParam = searchParams?.get("switchCompany");
  const [iFrameCompanySwitch, setIFrameCompanySwitch] = useState(() => {
    if (companySwitchQueryParam === COMPANY_IMPORTER) {
      return COMPANY_IMPORTER;
    }
    if (companySwitchQueryParam === COMPANY_EXPORTER) {
      return COMPANY_EXPORTER;
    }
    return null;
  });
  const iFrameCookieValue =
    typeof document !== "undefined" ? getCookie("iFrameView") : null;
  const isIFrameView =
    iFrameViewQueryParam !== null
      ? iFrameViewQueryParam === "true"
      : iFrameCookieValue === "true";
  const requestedIFrameCompanyType = isIFrameView
    ? iFrameCompanySwitch ?? COMPANY_EXPORTER
    : null;

  const { i18n } = useTranslation();

  useEffect(() => {
    if (detail) {
      i18n.changeLanguage(detail.locale);
      if(detail.locale === 'it'){
        moment.locale(detail.locale, italianLocale);
      }else{
        moment.locale(detail.locale);
      }

    }
    // eslint-disable-next-line
  }, [detail]);

  useEffect(() => {
    const unsubList = toSubscribeOnLoad.map((fn) => store.subscribe(fn));

    return () => {
      unsubList.forEach((fn) => {
        fn();
      });
    };
  }, []);

  useEffect( () => {
    dispatch(loadUserDataIfMissing());
    if (loggedCompany) {
        document.title = 'Dokless | ' + (loggedCompany?.business_name ?? 'The app that makes your business borderless');
       dispatch(processPermissionList());
       dispatch(initialPermissionsLoad());
       dispatch(subscriptionUsageLimits(loggedCompany.id));
    } else {
      if (detail && ((detail.default_type === USER_AGENT) || (detail.default_type === USER_EMPLOYEE)) && detail?.connections?.length === 0) {
        dispatch(setFakePermission())
      }
    }
    // eslint-disable-next-line
  }, [dispatch, loggedCompany]);

  useEffect(() => {
    if (!detail || !switchCompanyQueryParam) {
      return;
    }

    if (loggedCompany?.id && `${loggedCompany.id}` === switchCompanyQueryParam) {
      return;
    }

    const findById = (entity) =>
      entity?.id && `${entity.id}` === switchCompanyQueryParam;

    const ownerCompany =
      detail?.companies?.find(findById) ||
      (findById(detail?.company) ? detail?.company : null);

    if (ownerCompany) {
      dispatch(updateOwnerLoggedCompany(ownerCompany));
      dispatch(refreshPermissions({ company_id: ownerCompany.id }));
      dispatch(customersList({ company_id: ownerCompany.id }));
      return;
    }

    const connectedCompany = detail?.connections?.find((connection) =>
      findById(connection?.company)
    );

    if (connectedCompany?.company?.id) {
      dispatch(updateLoggedCompany(connectedCompany));
      dispatch(refreshPermissions({ company_id: connectedCompany.company.id }));
      dispatch(customersList({ company_id: connectedCompany.company.id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, switchCompanyQueryParam, loggedCompany, dispatch]);


  useEffect( () => {
    if (isSwitched !== undefined) {
        navigate(getWebPathParam([DASHBOARD]))
    }
    // eslint-disable-next-line
  }, [isSwitched]);

  useEffect(() => {
    const className = "sidebar-hidden";
    const { body } = document;

    if (sidebarHidden) {
      body.classList.add(className);
    } else {
      body.classList.remove(className);
    }

    if (
      typeof window !== "undefined" &&
      typeof window.localStorage !== "undefined"
    ) {
      window.localStorage.setItem(
        SIDEBAR_STORAGE_KEY,
        sidebarHidden ? "true" : "false"
      );
    }

    return () => {
      body.classList.remove(className);
    };
  }, [sidebarHidden]);

  useEffect(() => {
    if (
      companySwitchQueryParam !== COMPANY_IMPORTER &&
      companySwitchQueryParam !== COMPANY_EXPORTER
    ) {
      return;
    }

    setIFrameCompanySwitch(companySwitchQueryParam);
  }, [companySwitchQueryParam]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }

    setCookie("iFrameView", isIFrameView ? "true" : "false");
  }, [isIFrameView]);

  useEffect(() => {
    if (!isIFrameView || requestedIFrameCompanyType === null) {
      return;
    }

    dispatch(safeChangeCompany(requestedIFrameCompanyType));
  }, [dispatch, isIFrameView, requestedIFrameCompanyType]);

  if (loggedCompany && isUserCompanyProcessing(status)) {
    return <Loading />;
  }


  if (!isAuthenticated) {
    return <Navigate to={LOGIN} state={{ from: location }} />;
  }

  if (
    onboardingStatus !== undefined &&
    onboardingStatus !== userOnboardingStatus.CONFIRMED
  ) {
    return <Navigate to={ONBOARDING} />;
  }

  if (isIFrameView) {
    return (
        <>
            <Notification />
            <div className="flex h-screen overflow-hidden">
                <div className={`flex flex-col flex-1 transition-[padding-left] duration-300 ease-in-out`}>
                    <main id="scrollableDiv" className={"overflow-y-auto min-h-screen pb-[57px]"}>
                        <Outlet />
                    </main>
                </div>
            </div>
            <NotificationProgress />

        </>

    );
  }

  return (
    <>
      <div className="flex h-screen overflow-hidden">
        <Notification />

        {/* Sidebar */}
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          isHidden={sidebarHidden}
          setHidden={setSidebarHidden}
        />

        {sidebarHidden && (
          <button
            type="button"
            onClick={() => setSidebarHidden(false)}
            className="hidden md:flex fixed top-3 left-0 z-30 h-10 w-10 items-center justify-center text-blue-1000  "
            title="Mostra sidebar"
          >
            <PanelLeft className="h-5 w-5" />
          </button>
        )}

        <div
          className={`flex flex-col flex-1 transition-[padding-left] duration-300 ease-in-out ${
            sidebarHidden ? "md:pl-0" : "md:pl-64"
          }`}
        >
          <Header setSidebarOpen={setSidebarOpen} />
          <main id="scrollableDiv" className={'overflow-y-auto min-h-screen '}>
            <Outlet />
          </main>
        </div>
      </div>
      <NotificationProgress />
    </>

  );
};

export default RequireAuth;
